import { notFound } from "@tanstack/react-router";
import { BarChart3Icon, ChevronRightIcon, SparklesIcon } from "lucide-react";
import React from "react";
import { Disclosure, DisclosurePanel } from "react-aria-components";
import { useTitle } from "react-use";

import { hasPermission } from "@blis/shared/permissions";
import { trpc } from "@blis/trpc/react";

import { SubHeader } from "@/components/SubHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import { cn } from "@/components/ui/utils";
import { useAuth } from "@/features/auth/hooks";
import { InsightsExamplesSheet } from "../components/insights/InsightsExamplesSheet";
import {
  type InsightsAskResult,
  InsightsQueryPanel,
  type InsightsQueryPanelHandle,
} from "../components/insights/InsightsQueryPanel";
import { InsightsResultsGrid } from "../components/insights/InsightsResultsGrid";
import { InsightsResultsSkeleton } from "../components/insights/InsightsResultsSkeleton";
import { InsightsResultsToolbar } from "../components/insights/InsightsResultsToolbar";
import {
  InsightsSummaryCard,
  InsightsSummaryError,
} from "../components/insights/InsightsSummaryCard";
import { insightsSummaryCountLabel } from "../components/insights/insights-status";

export function InsightsPage() {
  useTitle("Insights");
  const { user } = useAuth();
  if (!hasPermission(user.role, "read:insights")) {
    throw notFound();
  }

  const queryPanelRef = React.useRef<InsightsQueryPanelHandle>(null);
  const [result, setResult] = React.useState<InsightsAskResult | null>(null);
  const [isFetching, setIsFetching] = React.useState(false);
  const [activeQuestion, setActiveQuestion] = React.useState<string | null>(
    null,
  );

  const { data: schemaInfo } = trpc.admin.insights.schema.useQuery();
  const exampleQuestions = schemaInfo?.exampleQuestions ?? [];
  const panelDisabled = isFetching || schemaInfo?.bedrockConfigured === false;

  const showResults = Boolean(result?.rows.length);
  const showEmpty = Boolean(result?.sql && result.rows.length === 0);

  function handleExampleSelect(example: string) {
    queryPanelRef.current?.runQuestion(example);
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
      <SubHeader>
        <SubHeader.Left className="gap-3">
          <div className="flex shrink-0 items-center gap-2">
            <h1 className="text-lg font-semibold">Insights</h1>
            <Badge variant="soft" color="amber" size="sm" className="shrink-0">
              Experimental
            </Badge>
          </div>
          <Text size="2" muted className="hidden min-w-0 truncate lg:block">
            Ask questions in plain English. Results and AI summaries may be
            incomplete or inaccurate while this feature is in early preview.
          </Text>
        </SubHeader.Left>
        <SubHeader.Right>
          <InsightsExamplesSheet
            examples={exampleQuestions}
            onSelect={handleExampleSelect}
            isDisabled={panelDisabled}
          />
        </SubHeader.Right>
      </SubHeader>

      <div className="flex shrink-0 flex-col gap-2 border-b px-4 py-2">
        <InsightsQueryPanel
          ref={queryPanelRef}
          onResult={setResult}
          onFetchingChange={setIsFetching}
          onActiveQuestionChange={setActiveQuestion}
        />
      </div>

      <ResultsArea
        result={result}
        onResultChange={setResult}
        activeQuestion={activeQuestion}
        isFetching={isFetching}
        showResults={showResults}
        showEmpty={showEmpty}
      />
    </div>
  );
}

function ResultsArea({
  result,
  onResultChange,
  activeQuestion,
  isFetching,
  showResults,
  showEmpty,
}: {
  result: InsightsAskResult | null;
  onResultChange: React.Dispatch<
    React.SetStateAction<InsightsAskResult | null>
  >;
  activeQuestion: string | null;
  isFetching: boolean;
  showResults: boolean;
  showEmpty: boolean;
}) {
  const summarizeMutation = trpc.admin.insights.summarize.useMutation({
    onSuccess: (data, variables) => {
      onResultChange((prev): InsightsAskResult | null => {
        if (!prev) return null;
        if (prev.rows !== variables.rows) return prev;
        return {
          ...prev,
          summary: data.summary,
          summaryError: data.summaryError,
          summaryLatencyMs: data.summaryLatencyMs,
          summarizeRequested: data.summarizeRequested,
          canSummarizeRows: data.canSummarizeRows,
          summarizeIneligibleReason: data.summarizeIneligibleReason,
        } as InsightsAskResult;
      });
    },
  });

  const [summaryExpanded, setSummaryExpanded] = React.useState(false);

  React.useEffect(() => {
    if (!result?.summary) {
      setSummaryExpanded(false);
      return;
    }
    if (result.summarizeRequested) {
      setSummaryExpanded(true);
    }
  }, [result?.summary, result?.summarizeRequested]);

  const sqlToShow = result?.sql ?? null;
  const showToolbar = Boolean(
    result && activeQuestion && !isFetching && (showResults || showEmpty),
  );

  function handleSummarizeThemes() {
    if (
      !result ||
      !activeQuestion ||
      isFetching ||
      summarizeMutation.isPending
    ) {
      return;
    }
    summarizeMutation.mutate({
      question: activeQuestion,
      rows: result.rows,
    });
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2 overflow-hidden px-4 pb-4 pt-2">
      {isFetching && !result ? <InsightsResultsSkeleton /> : null}

      {showToolbar && result ? (
        <InsightsResultsToolbar
          result={result}
          sql={sqlToShow}
          isSummarizing={summarizeMutation.isPending}
          onSummarizeThemes={handleSummarizeThemes}
        />
      ) : null}

      {result?.summary ? (
        <Disclosure
          isExpanded={summaryExpanded}
          onExpandedChange={setSummaryExpanded}
          className="group shrink-0 overflow-hidden rounded-lg border border-primary/20 bg-primary/5"
        >
          <Button
            variant="ghost"
            size="sm"
            slot="trigger"
            className="flex h-9 w-full items-center justify-between gap-3 rounded-none px-3 font-normal hover:bg-primary/10"
          >
            <span className="flex min-w-0 items-center gap-2">
              <ChevronRightIcon
                className={cn(
                  "size-4 shrink-0 transition-transform",
                  "group-data-[expanded=true]:rotate-90",
                )}
              />
              <SparklesIcon className="size-3.5 shrink-0 text-primary" />
              <span className="truncate text-sm font-medium">
                Theme summary
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground tabular-nums">
              <span>
                {result.summary.themes.length}{" "}
                {result.summary.themes.length === 1 ? "theme" : "themes"}
              </span>
              {result.summaryLatencyMs ? (
                <span>{result.summaryLatencyMs}ms</span>
              ) : null}
            </span>
          </Button>
          <DisclosurePanel className="group-data-[expanded=false]:hidden">
            <div className="max-h-[min(38vh,280px)] overflow-y-auto border-t border-primary/10 px-3 pb-3 pt-2">
              <InsightsSummaryCard
                summary={result.summary}
                countColumnLabel={insightsSummaryCountLabel(result.rows)}
              />
            </div>
          </DisclosurePanel>
        </Disclosure>
      ) : null}

      {result?.summaryError && !result.summary ? (
        <div className="shrink-0">
          <InsightsSummaryError error={result.summaryError} />
        </div>
      ) : null}

      {showResults && result ? (
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          <InsightsResultsGrid rows={result.rows} loading={isFetching} />
        </div>
      ) : showEmpty ? (
        <Text muted className="shrink-0 py-4">
          Query returned no rows.
        </Text>
      ) : !isFetching ? (
        <div className="flex flex-1 min-h-48 flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/15 px-6">
          <BarChart3Icon className="size-10 text-muted-foreground/50" />
          <Text size="2" muted className="text-center max-w-sm">
            Ask a question to see results here. Reason-style questions may
            include an automatic theme summary.
          </Text>
        </div>
      ) : null}
    </div>
  );
}
